import {
  getObject,
  getLeoUuid,
  LeoUUID,
  getUrl,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { FileAttributes, FileAttributesDTO } from "../types/fileAttributes";
/* eslint-disable import/export */
export abstract class GetPresignedUploadURLForQuestionOptionsRPC implements
  LeoRPC<GetPresignedUploadURLForQuestionOptionsRPC.Request, GetPresignedUploadURLForQuestionOptionsRPC.Response, GetPresignedUploadURLForQuestionOptionsRPC.Errors.Errors>
{
  abstract execute(
    request: GetPresignedUploadURLForQuestionOptionsRPC.Request
  ): Promise<LeoRPCResult<GetPresignedUploadURLForQuestionOptionsRPC.Response, GetPresignedUploadURLForQuestionOptionsRPC.Errors.Errors>>;
}

export namespace GetPresignedUploadURLForQuestionOptionsRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    fileAttributes: FileAttributesDTO;
    surveyId: string;
    projectId: string;
    questionId: string;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isGetPresignedUploadURLForQuestionOptionsRPCRequest: boolean = true;
  
    readonly fileAttributes: FileAttributes;
    readonly surveyId: LeoUUID;
    readonly projectId: LeoUUID;
    readonly questionId: LeoUUID;
  
  
    constructor(
    fileAttributes: FileAttributes, 
    surveyId: LeoUUID, 
    projectId: LeoUUID, 
    questionId: LeoUUID
    ) {
      this.fileAttributes = fileAttributes;
      this.surveyId = surveyId;
      this.projectId = projectId;
      this.questionId = questionId;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const fileAttributes = FileAttributes.fromDTO(getObject(dto, "fileAttributes", InvalidRequestFileAttributesError));
      const surveyId = getLeoUuid(dto, "surveyId", InvalidRequestSurveyIdError);
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      const questionId = getLeoUuid(dto, "questionId", InvalidRequestQuestionIdError);
      return new Request(
        fileAttributes,
        surveyId,
        projectId,
        questionId
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          fileAttributes: this.fileAttributes.toDTO(),
          surveyId: this.surveyId.toDTO(),
          projectId: this.projectId.toDTO(),
          questionId: this.questionId.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `GetPresignedUploadURLForQuestionOptionsRPC(fileAttributes='${this.fileAttributes}',surveyId='${this.surveyId}',projectId='${this.projectId}',questionId='${this.questionId}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    documentURL: string;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isGetPresignedUploadURLForQuestionOptionsResponse: boolean = true;
  
    readonly documentURL: URL;
  
  
    constructor(
    documentURL: URL
    ) {
      this.documentURL = documentURL;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const documentURL = getUrl(dto, "documentURL", InvalidResponseDocumentUrlError);
      return new Response(
        documentURL
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          documentURL: this.documentURL.toString()
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `GetPresignedUploadURLForQuestionOptions(documentURL='${this.documentURL}')`;
  };
  

  export enum RPCError {
    InvalidSurveyId = "INVALID_SURVEY_ID",
    InvalidProjectId = "INVALID_PROJECT_ID",
    ProjectAlreadyArchived = "PROJECT_ALREADY_ARCHIVED",
    InvalidQuestionId = "INVALID_QUESTION_ID",
    QuestionWasDeleted = "QUESTION_WAS_DELETED",
    IncorrectQuestionType = "INCORRECT_QUESTION_TYPE",
    SurveyIsClosed = "SURVEY_IS_CLOSED",
    SurveyIsLive = "SURVEY_IS_LIVE"
  }

  export namespace Errors {
    
    export interface InvalidSurveyIdDTO {
      code: string;
    }
    
    export class InvalidSurveyId extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyId;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyIdDTO {
        const dto: InvalidSurveyIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyId.prototype.toString = function toString(): string {
      return `GetPresignedUploadURLForQuestionOptionsRPC.INVALID_SURVEY_ID()`;
    };
    
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `GetPresignedUploadURLForQuestionOptionsRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface ProjectAlreadyArchivedDTO {
      code: string;
    }
    
    export class ProjectAlreadyArchived extends LeoRPCError<RPCError> {
      readonly isProjectAlreadyArchived: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ProjectAlreadyArchived;
        super(code);
      }
    
    
      toDTO(): ProjectAlreadyArchivedDTO {
        const dto: ProjectAlreadyArchivedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ProjectAlreadyArchived.prototype.toString = function toString(): string {
      return `GetPresignedUploadURLForQuestionOptionsRPC.PROJECT_ALREADY_ARCHIVED()`;
    };
    
    
    export interface InvalidQuestionIdDTO {
      code: string;
    }
    
    export class InvalidQuestionId extends LeoRPCError<RPCError> {
      readonly isInvalidQuestionId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidQuestionId;
        super(code);
      }
    
    
      toDTO(): InvalidQuestionIdDTO {
        const dto: InvalidQuestionIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidQuestionId.prototype.toString = function toString(): string {
      return `GetPresignedUploadURLForQuestionOptionsRPC.INVALID_QUESTION_ID()`;
    };
    
    
    export interface QuestionWasDeletedDTO {
      code: string;
    }
    
    export class QuestionWasDeleted extends LeoRPCError<RPCError> {
      readonly isQuestionWasDeleted: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.QuestionWasDeleted;
        super(code);
      }
    
    
      toDTO(): QuestionWasDeletedDTO {
        const dto: QuestionWasDeletedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    QuestionWasDeleted.prototype.toString = function toString(): string {
      return `GetPresignedUploadURLForQuestionOptionsRPC.QUESTION_WAS_DELETED()`;
    };
    
    
    export interface IncorrectQuestionTypeDTO {
      code: string;
    }
    
    export class IncorrectQuestionType extends LeoRPCError<RPCError> {
      readonly isIncorrectQuestionType: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.IncorrectQuestionType;
        super(code);
      }
    
    
      toDTO(): IncorrectQuestionTypeDTO {
        const dto: IncorrectQuestionTypeDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    IncorrectQuestionType.prototype.toString = function toString(): string {
      return `GetPresignedUploadURLForQuestionOptionsRPC.INCORRECT_QUESTION_TYPE()`;
    };
    
    
    export interface SurveyIsClosedDTO {
      code: string;
    }
    
    export class SurveyIsClosed extends LeoRPCError<RPCError> {
      readonly isSurveyIsClosed: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.SurveyIsClosed;
        super(code);
      }
    
    
      toDTO(): SurveyIsClosedDTO {
        const dto: SurveyIsClosedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    SurveyIsClosed.prototype.toString = function toString(): string {
      return `GetPresignedUploadURLForQuestionOptionsRPC.SURVEY_IS_CLOSED()`;
    };
    
    
    export interface SurveyIsLiveDTO {
      code: string;
    }
    
    export class SurveyIsLive extends LeoRPCError<RPCError> {
      readonly isSurveyIsLive: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.SurveyIsLive;
        super(code);
      }
    
    
      toDTO(): SurveyIsLiveDTO {
        const dto: SurveyIsLiveDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    SurveyIsLive.prototype.toString = function toString(): string {
      return `GetPresignedUploadURLForQuestionOptionsRPC.SURVEY_IS_LIVE()`;
    };
    

    export type Errors = InvalidSurveyId | InvalidProjectId | ProjectAlreadyArchived | InvalidQuestionId | QuestionWasDeleted | IncorrectQuestionType | SurveyIsClosed | SurveyIsLive;
    export type ErrorsDTO = InvalidSurveyIdDTO | InvalidProjectIdDTO | ProjectAlreadyArchivedDTO | InvalidQuestionIdDTO | QuestionWasDeletedDTO | IncorrectQuestionTypeDTO | SurveyIsClosedDTO | SurveyIsLiveDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidSurveyId:
          return new InvalidSurveyId();
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.ProjectAlreadyArchived:
          return new ProjectAlreadyArchived();
        case RPCError.InvalidQuestionId:
          return new InvalidQuestionId();
        case RPCError.QuestionWasDeleted:
          return new QuestionWasDeleted();
        case RPCError.IncorrectQuestionType:
          return new IncorrectQuestionType();
        case RPCError.SurveyIsClosed:
          return new SurveyIsClosed();
        case RPCError.SurveyIsLive:
          return new SurveyIsLive();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestFileAttributesError extends GetPresignedUploadURLForQuestionOptionsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestFileAttributesError";
  }
}

export class InvalidRequestSurveyIdError extends GetPresignedUploadURLForQuestionOptionsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyIdError";
  }
}

export class InvalidRequestProjectIdError extends GetPresignedUploadURLForQuestionOptionsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidRequestQuestionIdError extends GetPresignedUploadURLForQuestionOptionsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestQuestionIdError";
  }
}

export class InvalidResponseDocumentUrlError extends GetPresignedUploadURLForQuestionOptionsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseDocumentUrlError";
  }
}

export class InvalidResponseInvalidSurveyIdError extends GetPresignedUploadURLForQuestionOptionsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyIdError";
  }
}

export class InvalidResponseInvalidProjectIdError extends GetPresignedUploadURLForQuestionOptionsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseProjectAlreadyArchivedError extends GetPresignedUploadURLForQuestionOptionsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectAlreadyArchivedError";
  }
}

export class InvalidResponseInvalidQuestionIdError extends GetPresignedUploadURLForQuestionOptionsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidQuestionIdError";
  }
}

export class InvalidResponseQuestionWasDeletedError extends GetPresignedUploadURLForQuestionOptionsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseQuestionWasDeletedError";
  }
}

export class InvalidResponseIncorrectQuestionTypeError extends GetPresignedUploadURLForQuestionOptionsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseIncorrectQuestionTypeError";
  }
}

export class InvalidResponseSurveyIsClosedError extends GetPresignedUploadURLForQuestionOptionsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyIsClosedError";
  }
}

export class InvalidResponseSurveyIsLiveError extends GetPresignedUploadURLForQuestionOptionsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyIsLiveError";
  }
}

